import React, { useCallback, useRef, useState, useEffect } from 'react'
import { useEditor, EditorContent, Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import TextStyle from '@tiptap/extension-text-style'
import { Extension } from '@tiptap/core'
import { uploadService, isValidImageFile, MAX_IMAGE_SIZE, formatFileSize } from '@/services/uploadService'

// Font Size extension
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (size: string) => ReturnType
      unsetFontSize: () => ReturnType
    }
  }
}

const FontSize = Extension.create({
  name: 'fontSize',

  addOptions() {
    return {
      types: ['textStyle'],
    }
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => {
              // Parse font-size from inline style
              const fontSize = element.style.fontSize
              return fontSize || null
            },
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {}
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              }
            },
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      setFontSize: (fontSize: string) => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run()
      },
      unsetFontSize: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run()
      },
    }
  },
})

// Custom Image extension with resize and alignment support
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: element => element.style.width || null,
        renderHTML: () => ({}), // Rendered via style attribute
      },
      align: {
        default: 'left',
        parseHTML: element => {
          const marginLeft = element.style.marginLeft
          const marginRight = element.style.marginRight
          if (marginLeft === 'auto' && marginRight === 'auto') return 'center'
          if (marginLeft === 'auto') return 'right'
          return 'left'
        },
        renderHTML: () => ({}), // Rendered via style attribute
      },
    }
  },

  renderHTML({ HTMLAttributes, node }) {
    const { width, align } = node.attrs
    const styles = []

    // Add width style
    if (width) {
      styles.push(`width: ${width}`)
    }

    // Add display and alignment styles
    styles.push('display: block')
    if (align === 'center') {
      styles.push('margin-left: auto', 'margin-right: auto')
    } else if (align === 'right') {
      styles.push('margin-left: auto')
    }

    return [
      'img',
      {
        ...HTMLAttributes,
        style: styles.join('; '),
      },
    ]
  },
})

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: string
  disabled?: boolean
}

interface MenuButtonProps {
  editor: Editor | null
  action: () => void
  isActive?: boolean
  icon: string
  title: string
  disabled?: boolean
}

const MenuButton: React.FC<MenuButtonProps> = ({
  editor,
  action,
  isActive = false,
  icon,
  title,
  disabled = false,
}) => {
  if (!editor) return null

  return (
    <button
      type="button"
      onClick={action}
      disabled={disabled}
      title={title}
      className={`p-2 rounded-lg transition-colors ${
        isActive
          ? 'bg-primary text-black'
          : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-white/10'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span className="material-symbols-outlined text-lg">{icon}</span>
    </button>
  )
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start writing...',
  minHeight = '200px',
  disabled = false,
}) => {
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [showImageControls, setShowImageControls] = useState(false)
  const [customWidth, setCustomWidth] = useState('')
  const imageInputRef = useRef<HTMLInputElement>(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      TextStyle,
      FontSize,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline',
        },
      }),
      CustomImage.configure({
        HTMLAttributes: {
          class: 'rounded-lg max-w-full h-auto',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    onSelectionUpdate: ({ editor }) => {
      // Show image controls if image is selected
      const isImage = editor.isActive('image')
      setShowImageControls(isImage)
    },
  })

  // Update image controls visibility when editor state changes
  useEffect(() => {
    if (!editor) return

    const handleUpdate = () => {
      setShowImageControls(editor.isActive('image'))
    }

    editor.on('selectionUpdate', handleUpdate)
    return () => {
      editor.off('selectionUpdate', handleUpdate)
    }
  }, [editor])

  const setLink = useCallback(() => {
    if (!editor) return

    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt('Enter URL:', previousUrl)

    if (url === null) return

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }, [editor])

  const handleImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !editor) return

    // Validate file
    if (!isValidImageFile(file)) {
      setUploadError('Invalid image format. Allowed: JPG, PNG, GIF, WebP')
      return
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setUploadError(`Image too large. Maximum size: ${formatFileSize(MAX_IMAGE_SIZE)}`)
      return
    }

    setUploadError(null)
    setIsUploadingImage(true)

    try {
      const response = await uploadService.uploadImage(file)

      if (response.success && response.data) {
        // Insert image at current cursor position
        editor.chain().focus().setImage({ src: response.data.url }).run()
      } else {
        throw new Error('Upload failed')
      }
    } catch (err) {
      console.error('Image upload error:', err)
      setUploadError(err instanceof Error ? err.message : 'Failed to upload image')
    } finally {
      setIsUploadingImage(false)
      // Reset file input
      if (imageInputRef.current) {
        imageInputRef.current.value = ''
      }
    }
  }, [editor])

  const addImage = useCallback(() => {
    if (!editor || disabled || isUploadingImage) return
    // Trigger file picker
    imageInputRef.current?.click()
  }, [editor, disabled, isUploadingImage])

  // Image resize functions
  const setImageWidth = useCallback((width: string) => {
    if (!editor) return
    editor.chain().focus().updateAttributes('image', { width }).run()
  }, [editor])

  // Image alignment functions
  const setImageAlign = useCallback((align: 'left' | 'center' | 'right') => {
    if (!editor) return
    editor.chain().focus().updateAttributes('image', { align }).run()
  }, [editor])

  // Apply custom width
  const applyCustomWidth = useCallback(() => {
    if (!editor || !customWidth.trim()) return

    // Validate input (allow px, %, or number)
    let width = customWidth.trim()

    // If just number, add px
    if (/^\d+$/.test(width)) {
      width = width + 'px'
    }

    // Validate format (must be number followed by px or %)
    if (!/^\d+(%|px)$/.test(width)) {
      setUploadError('Invalid width format. Use: 300px or 50%')
      return
    }

    editor.chain().focus().updateAttributes('image', { width }).run()
    setCustomWidth('')
    setUploadError(null)
  }, [editor, customWidth])

  if (!editor) return null

  return (
    <div className="rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5">
        {/* Text formatting */}
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          icon="format_bold"
          title="Bold (Ctrl+B)"
          disabled={disabled}
        />
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          icon="format_italic"
          title="Italic (Ctrl+I)"
          disabled={disabled}
        />
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          icon="strikethrough_s"
          title="Strikethrough"
          disabled={disabled}
        />

        <div className="w-px h-6 bg-gray-100 dark:bg-white/10 mx-1" />

        {/* Font Size */}
        <div className="flex items-center gap-1">
          <select
            onChange={(e) => {
              const size = e.target.value
              if (size === 'default') {
                editor.chain().focus().unsetFontSize().run()
              } else {
                editor.chain().focus().setFontSize(size).run()
              }
            }}
            disabled={disabled}
            className="px-3 py-1.5 text-xs bg-[#1a1a1a] border border-gray-300 dark:border-white/20 rounded text-gray-200 hover:bg-[#252525] hover:border-primary/50 focus:outline-none focus:border-primary focus:bg-[#252525] cursor-pointer transition-colors"
            style={{
              backgroundImage: 'none',
              appearance: 'auto',
            }}
            title="Font Size"
          >
            <option value="default" className="bg-[#1a1a1a] text-gray-200">Size</option>
            <option value="12px" className="bg-[#1a1a1a] text-gray-200">12px</option>
            <option value="14px" className="bg-[#1a1a1a] text-gray-200">14px</option>
            <option value="16px" className="bg-[#1a1a1a] text-gray-200">16px (Normal)</option>
            <option value="18px" className="bg-[#1a1a1a] text-gray-200">18px</option>
            <option value="20px" className="bg-[#1a1a1a] text-gray-200">20px</option>
            <option value="24px" className="bg-[#1a1a1a] text-gray-200">24px</option>
            <option value="28px" className="bg-[#1a1a1a] text-gray-200">28px</option>
            <option value="32px" className="bg-[#1a1a1a] text-gray-200">32px</option>
            <option value="36px" className="bg-[#1a1a1a] text-gray-200">36px</option>
            <option value="48px" className="bg-[#1a1a1a] text-gray-200">48px</option>
          </select>
        </div>

        <div className="w-px h-6 bg-gray-100 dark:bg-white/10 mx-1" />

        {/* Headings */}
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          icon="format_h1"
          title="Heading 1"
          disabled={disabled}
        />
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          icon="format_h2"
          title="Heading 2"
          disabled={disabled}
        />
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          icon="format_h3"
          title="Heading 3"
          disabled={disabled}
        />

        <div className="w-px h-6 bg-gray-100 dark:bg-white/10 mx-1" />

        {/* Text Alignment */}
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          icon="format_align_left"
          title="Align Left"
          disabled={disabled}
        />
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          icon="format_align_center"
          title="Align Center"
          disabled={disabled}
        />
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          icon="format_align_right"
          title="Align Right"
          disabled={disabled}
        />
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().setTextAlign('justify').run()}
          isActive={editor.isActive({ textAlign: 'justify' })}
          icon="format_align_justify"
          title="Justify"
          disabled={disabled}
        />

        <div className="w-px h-6 bg-gray-100 dark:bg-white/10 mx-1" />

        {/* Lists */}
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          icon="format_list_bulleted"
          title="Bullet List"
          disabled={disabled}
        />
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          icon="format_list_numbered"
          title="Numbered List"
          disabled={disabled}
        />

        <div className="w-px h-6 bg-gray-100 dark:bg-white/10 mx-1" />

        {/* Block elements */}
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          icon="format_quote"
          title="Blockquote"
          disabled={disabled}
        />
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().toggleCodeBlock().run()}
          isActive={editor.isActive('codeBlock')}
          icon="code"
          title="Code Block"
          disabled={disabled}
        />

        <div className="w-px h-6 bg-gray-100 dark:bg-white/10 mx-1" />

        {/* Links and media */}
        <MenuButton
          editor={editor}
          action={setLink}
          isActive={editor.isActive('link')}
          icon="link"
          title="Insert Link"
          disabled={disabled}
        />
        <MenuButton
          editor={editor}
          action={addImage}
          isActive={false}
          icon={isUploadingImage ? "hourglass_empty" : "image"}
          title={isUploadingImage ? "Uploading image..." : "Insert Image"}
          disabled={disabled || isUploadingImage}
        />

        <div className="w-px h-6 bg-gray-100 dark:bg-white/10 mx-1" />

        {/* History */}
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().undo().run()}
          isActive={false}
          icon="undo"
          title="Undo (Ctrl+Z)"
          disabled={disabled || !editor.can().undo()}
        />
        <MenuButton
          editor={editor}
          action={() => editor.chain().focus().redo().run()}
          isActive={false}
          icon="redo"
          title="Redo (Ctrl+Y)"
          disabled={disabled || !editor.can().redo()}
        />
      </div>

      {/* Image Controls - Show when image is selected */}
      {showImageControls && !disabled && (
        <div className="flex items-center gap-4 px-4 py-2 border-b border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 flex-wrap">
          <span className="text-sm text-gray-500 dark:text-gray-400">Image Settings:</span>

          {/* Preset Size Controls */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500 mr-1">Preset:</span>
            <button
              type="button"
              onClick={() => setImageWidth('300px')}
              className="px-2 py-1 text-xs rounded bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300"
              title="Small (300px)"
            >
              S
            </button>
            <button
              type="button"
              onClick={() => setImageWidth('500px')}
              className="px-2 py-1 text-xs rounded bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300"
              title="Medium (500px)"
            >
              M
            </button>
            <button
              type="button"
              onClick={() => setImageWidth('700px')}
              className="px-2 py-1 text-xs rounded bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300"
              title="Large (700px)"
            >
              L
            </button>
            <button
              type="button"
              onClick={() => setImageWidth('100%')}
              className="px-2 py-1 text-xs rounded bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300"
              title="Full (100%)"
            >
              Full
            </button>
          </div>

          <div className="w-px h-6 bg-gray-100 dark:bg-white/10" />

          {/* Custom Size Input */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Custom:</span>
            <input
              type="text"
              value={customWidth}
              onChange={(e) => setCustomWidth(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  applyCustomWidth()
                }
              }}
              placeholder="300px or 60%"
              className="w-28 px-2 py-1 text-xs bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded text-gray-600 dark:text-gray-300 placeholder-gray-600 focus:outline-none focus:border-primary"
            />
            <button
              type="button"
              onClick={applyCustomWidth}
              disabled={!customWidth.trim()}
              className="px-3 py-1 text-xs rounded bg-primary text-black hover:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              title="Apply custom width"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={() => {
                editor?.chain().focus().updateAttributes('image', { width: null }).run()
                setCustomWidth('')
              }}
              className="px-2 py-1 text-xs rounded bg-gray-100 dark:bg-white/5 hover:bg-red-500/20 text-gray-500 dark:text-gray-400 hover:text-red-400"
              title="Reset to original size"
            >
              <span className="material-symbols-outlined text-[16px]">restart_alt</span>
            </button>
          </div>

          <div className="w-px h-6 bg-gray-100 dark:bg-white/10" />

          {/* Alignment Controls */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500 mr-1">Align:</span>
            <button
              type="button"
              onClick={() => setImageAlign('left')}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300"
              title="Align Left"
            >
              <span className="material-symbols-outlined text-lg">format_align_left</span>
            </button>
            <button
              type="button"
              onClick={() => setImageAlign('center')}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300"
              title="Align Center"
            >
              <span className="material-symbols-outlined text-lg">format_align_center</span>
            </button>
            <button
              type="button"
              onClick={() => setImageAlign('right')}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300"
              title="Align Right"
            >
              <span className="material-symbols-outlined text-lg">format_align_right</span>
            </button>
          </div>

          <div className="ml-auto text-xs text-primary">
            ✓ Image selected - Click away to deselect
          </div>
        </div>
      )}

      {/* Editor content */}
      <EditorContent
        editor={editor}
        className="p-4 focus:outline-none text-slate-900 dark:text-slate-100"
        style={{ minHeight }}
      />

      {/* Hidden file input for image upload */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
        onChange={handleImageUpload}
        className="hidden"
      />

      {/* Upload status */}
      {isUploadingImage && (
        <div className="px-4 py-2 border-t border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5">
          <div className="flex items-center gap-2 text-sm text-primary">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
            <span>Uploading image...</span>
          </div>
        </div>
      )}

      {/* Error message */}
      {uploadError && (
        <div className="px-4 py-2 border-t border-gray-200 dark:border-white/10 bg-red-500/10">
          <div className="flex items-center gap-2 text-sm text-red-400">
            <span className="material-symbols-outlined text-[18px]">error</span>
            <span>{uploadError}</span>
            <button
              onClick={() => setUploadError(null)}
              className="ml-auto p-1 hover:bg-gray-200 dark:hover:bg-white/10 rounded"
            >
              <span className="material-symbols-outlined text-[16px]">close</span>
            </button>
          </div>
        </div>
      )}

      {/* Editor styles */}
      <style>{`
        .ProseMirror {
          min-height: ${minHeight};
          outline: none;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #94a3b8;
          pointer-events: none;
          height: 0;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror h1 {
          font-size: 1.75rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
          color: inherit;
        }
        .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
          color: inherit;
        }
        .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
          color: inherit;
        }
        .ProseMirror p {
          margin-bottom: 0.75rem;
          color: inherit;
        }
        .ProseMirror ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .ProseMirror ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .ProseMirror li {
          margin-bottom: 0.25rem;
          color: inherit;
        }
        .ProseMirror blockquote {
          border-left: 4px solid #facc15;
          padding-left: 1rem;
          color: #64748b;
          font-style: italic;
          margin: 1.5rem 0;
          background: rgba(250, 204, 21, 0.05);
          padding-top: 0.5rem;
          padding-bottom: 0.5rem;
          border-radius: 0 0.5rem 0.5rem 0;
        }
        .ProseMirror pre {
          background: #0f172a;
          color: #e2e8f0;
          padding: 1.25rem;
          border-radius: 0.75rem;
          overflow-x: auto;
          margin: 1.5rem 0;
          border: 1px solid rgba(255,255,255,0.05);
        }
        .ProseMirror code {
          background: rgba(244, 63, 94, 0.1);
          padding: 0.2rem 0.4rem;
          border-radius: 0.4rem;
          color: #f43f5e;
          font-weight: 600;
          font-size: 0.85em;
        }
        .ProseMirror a {
          color: #3b82f6;
          text-decoration: underline;
          text-underline-offset: 2px;
          font-weight: 600;
        }
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 1rem;
          margin: 1.5rem 0;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }
        .ProseMirror img.ProseMirror-selectednode {
          outline: 4px solid #3b82f6;
          outline-offset: 4px;
        }
        .ProseMirror img:hover {
          transform: scale(1.01);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  )
}

export default RichTextEditor
